import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  authApi,
  googleStartUrl,
  isMfaChallenge,
  providerStartUrl,
} from "../../api/auth";
import { API_URL } from "../../api/client";
import { useBranding } from "../../shared/branding";
import { AuthShell } from "../../shared/AuthShell";
import { errorMessage } from "../../shared/errors";
import { useInvalidateMe, useMe } from "../../shared/hooks/useMe";
import { navigateAfterAuth, safeReturnTo } from "../../shared/returnTo";
import { Turnstile } from "../../shared/Turnstile";
import { getPasskey, passkeysSupported } from "../../shared/webauthn";
import { Alert, Button, Input } from "../../ui";
import { RegisterForm } from "./RegisterForm";

const ERROR_TEXT: Record<string, string> = {
  access_denied: "The sign-in was cancelled.",
  provider_error:
    "The sign-in provider did not complete the sign-in (an email address is required). Please try again.",
  provider_disabled: "This sign-in method is not available yet.",
  invalid_nonce:
    "The sign-in response did not match the request. Please try again.",
  identity_taken: "That account is already linked to another Intelibo user.",
  email_taken:
    "An account with this email exists. Sign in with your password, then link the provider under Sign-in methods.",
  invalid_state: "The sign-in link expired. Please try again.",
  login_required: "Please sign in to continue.",
};

export function LoginPage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const returnTo = safeReturnTo(search.get("return_to"));
  const clientId = search.get("client_id");
  const reauth = search.get("reauth") === "1";
  const [screen, setScreen] = useState<"login" | "register">(
    search.get("screen") === "register" ? "register" : "login",
  );
  const branding = useBranding(clientId);
  const me = useMe();
  const invalidateMe = useInvalidateMe();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [error, setError] = useState<string | null>(
    search.get("error")
      ? (ERROR_TEXT[search.get("error") as string] ?? "Sign-in failed.")
      : null,
  );
  const [busy, setBusy] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(
    search.get("mfa_token"),
  );
  const [mfaCode, setMfaCode] = useState("");
  const onToken = useCallback((token: string | null) => setCaptcha(token), []);

  useEffect(() => {
    if (me.data && reauth && !email) setEmail(me.data.email);
  }, [me.data, reauth, email]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await authApi.loginPassword({
        email,
        password,
        turnstile_token: captcha ?? undefined,
        return_to: returnTo ?? undefined,
      });
      if (isMfaChallenge(result)) {
        setMfaToken(result.mfa_token);
        return;
      }
      await invalidateMe();
      navigateAfterAuth(result.return_to ?? returnTo, navigate);
    } catch (err) {
      const details = (err as { details?: { captcha_required?: boolean } })
        .details;
      if (details?.captcha_required) setCaptchaRequired(true);
      setError(errorMessage(err, "Sign-in failed."));
    } finally {
      setBusy(false);
    }
  };

  const withPasskey = async (mode: "login" | "mfa") => {
    setBusy(true);
    setError(null);
    try {
      const options =
        mode === "mfa" && mfaToken
          ? await authApi.mfaPasskeyOptions(mfaToken)
          : await authApi.passkeyOptions(email || undefined);
      const credential = await getPasskey(options);
      const result =
        mode === "mfa" && mfaToken
          ? await authApi.loginMfaPasskey({ mfa_token: mfaToken, credential })
          : await authApi.loginPasskey({
              credential,
              return_to: returnTo ?? undefined,
            });
      await invalidateMe();
      navigateAfterAuth(result.return_to ?? returnTo, navigate);
    } catch (err) {
      setError(errorMessage(err, "Passkey sign-in did not complete."));
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e: FormEvent) => {
    e.preventDefault();
    if (!mfaToken) return;
    setBusy(true);
    setError(null);
    try {
      const result = await authApi.loginMfa({
        mfa_token: mfaToken,
        code: mfaCode,
      });
      await invalidateMe();
      navigateAfterAuth(result.return_to ?? returnTo, navigate);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 401 || status === 429) {
        // The attempt is over, not the code: drop the token from the URL too, or a reload
        // puts this form back with the same dead token and every correct code is refused.
        setMfaToken(null);
        setMfaCode("");
        const clean = new URLSearchParams(search);
        clean.delete("mfa_token");
        navigate(
          { pathname: "/login", search: clean.toString() },
          { replace: true },
        );
        setError(
          errorMessage(
            err,
            "The sign-in attempt expired. Please sign in again.",
          ),
        );
        return;
      }
      setError(errorMessage(err, "The code is not valid."));
    } finally {
      setBusy(false);
    }
  };

  const title =
    screen === "register"
      ? `Create your account for ${branding.name}`
      : reauth
        ? "Confirm it is you"
        : `Sign in to ${branding.name}`;
  if (mfaToken) {
    return (
      <AuthShell branding={branding} title="Enter your verification code">
        <form
          onSubmit={submitMfa}
          className="flex flex-col gap-4"
          aria-label="Two-factor code"
        >
          {error ? <Alert kind="error">{error}</Alert> : null}
          <p className="text-sm text-slate-600">
            Open your authenticator app and enter the six-digit code, or use one
            of your recovery codes.
          </p>
          <Input
            label="Code"
            autoComplete="one-time-code"
            inputMode="numeric"
            required
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
          />
          <Button type="submit" loading={busy}>
            Verify
          </Button>
          {passkeysSupported() ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => void withPasskey("mfa")}
              loading={busy}
            >
              Use a passkey instead
            </Button>
          ) : null}
          <button
            type="button"
            className="text-sm text-brand underline"
            onClick={() => {
              setMfaToken(null);
              setMfaCode("");
              setError(null);
            }}
          >
            Start over
          </button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell branding={branding} title={title}>
      {screen === "register" ? (
        <RegisterForm returnTo={returnTo} onSwitch={() => setScreen("login")} />
      ) : (
        <form
          onSubmit={submit}
          className="flex flex-col gap-4"
          aria-label="Sign in"
        >
          {error ? <Alert kind="error">{error}</Alert> : null}
          {reauth && me.data ? (
            <Alert kind="info">
              Signed in as {me.data.email}. Enter your password to continue.
            </Alert>
          ) : null}
          <a
            href={googleStartUrl(API_URL, returnTo)}
            className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50"
          >
            Continue with Google
          </a>
          <a
            href={providerStartUrl(API_URL, "facebook", returnTo)}
            className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50"
          >
            Continue with Facebook
          </a>
          <a
            href={providerStartUrl(API_URL, "apple", returnTo)}
            className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50"
          >
            Continue with Apple
          </a>
          {passkeysSupported() ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => void withPasskey("login")}
              loading={busy}
            >
              Sign in with a passkey
            </Button>
          ) : null}
          <div className="text-center text-xs uppercase text-slate-400">or</div>
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            label="Password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {captchaRequired ? <Turnstile onToken={onToken} /> : null}
          <Button type="submit" loading={busy}>
            Sign in
          </Button>
          <div className="flex justify-between text-sm">
            <button
              type="button"
              className="text-brand underline"
              onClick={() => setScreen("register")}
            >
              Create an account
            </button>
            <Link className="text-brand underline" to="/forgot-password">
              Forgot password?
            </Link>
          </div>
        </form>
      )}
    </AuthShell>
  );
}
